import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Radio, MapPin, Users, Clock, Navigation, Loader2, CheckCircle, XCircle, Car } from 'lucide-react';
import { API } from '../utils/apiConfig';
import { useDriverMatching, useLiveLocation } from '../hooks/useSocket';
import { speakDriverOnline, speakMatchFound } from '../utils/tts';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import './DriverOnline.css';

const DriverOnline = () => {
  const { t } = useTranslation();
  const { connected, isOnline, matchRequests, goOnline, acceptMatch, rejectMatch } = useDriverMatching();
  const { startTracking, stopTracking } = useLiveLocation();

  const [user, setUser] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [startCity, setStartCity] = useState('');
  const [destCity, setDestCity] = useState('');
  const [startCoords, setStartCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);
  const [maxSeats, setMaxSeats] = useState(3);
  const [maxDetour, setMaxDetour] = useState(5);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/auth/me`, { headers }).then(r => setUser(r.data)).catch(() => {});
    axios.get(`${API}/users/my-vehicle`, { headers }).then(r => setVehicle(r.data)).catch(() => {});
  }, []);

  // Announce new match requests via TTS
  useEffect(() => {
    if (matchRequests.length > 0) {
      const latest = matchRequests[matchRequests.length - 1];
      speakMatchFound(latest.guest_name || 'Mitfahrer');
    }
  }, [matchRequests.length]);

  const handleGoOnline = async () => {
    if (!startCity || !destCity) {
      setStatusMessage(t('driver.enterRoute', 'Bitte Start und Ziel eingeben'));
      return;
    }

    setLoading(true);
    setStatusMessage('');

    const driverData = {
      user_id: user?.id,
      name: user?.name || '',
      vehicle: vehicle ? { make: vehicle.make, model: vehicle.model, license_plate: vehicle.license_plate } : null,
      startAddress: startCity,
      endAddress: destCity,
      maxSeats: maxSeats,
      max_detour_km: maxDetour,
    };

    goOnline(driverData);
    startTracking();

    // TTS feedback
    speakDriverOnline();
    setLoading(false);
  };

  const handleGoOffline = () => {
    stopTracking();
    window.location.reload();
  };

  const handleAccept = (request) => {
    acceptMatch({
      guest_sid: request.guest_sid,
      driver_id: user?.id,
      driver_name: user?.name,
      vehicle: vehicle,
    });
  };

  const handleReject = (request) => {
    rejectMatch({
      guest_sid: request.guest_sid,
      reason: 'declined',
    });
  };

  return (
    <div className="driver-online-page" data-testid="driver-online-page">
      <div className="driver-online-container">
        {/* Header */}
        <div className="do-header">
          <Radio size={24} className={isOnline ? 'pulse-icon' : ''} />
          <h1>{isOnline ? t('driver.online', 'Du bist Online') : t('driver.goOnline', 'Online gehen')}</h1>
          <div className={`connection-badge ${connected ? 'connected' : 'disconnected'}`}>
            {connected ? 'Verbunden' : 'Verbindung...'}
          </div>
        </div>

        {!isOnline ? (
          /* ── Setup Form ── */
          <div className="do-setup" data-testid="driver-setup-form">
            <div className="do-card">
              <h2><Navigation size={18} /> Deine Route</h2>

              <AddressAutocomplete
                value={startCity}
                onChange={setStartCity}
                onSelect={(s) => setStartCoords({ lat: s.lat, lng: s.lng })}
                placeholder="Start (z.B. Kiel Hauptbahnhof)"
                label="Von"
                testId="driver-start-input"
              />

              <AddressAutocomplete
                value={destCity}
                onChange={setDestCity}
                onSelect={(s) => setDestCoords({ lat: s.lat, lng: s.lng })}
                placeholder="Ziel (z.B. Hamburg Uni)"
                label="Nach"
                testId="driver-dest-input"
              />

              <div className="do-params">
                <div className="do-param">
                  <label><Users size={16} /> Freie Plätze</label>
                  <input
                    type="number" value={maxSeats} min={1} max={8}
                    onChange={(e) => setMaxSeats(parseInt(e.target.value) || 1)}
                    data-testid="driver-seats-input"
                  />
                </div>
                <div className="do-param">
                  <label><Navigation size={16} /> Max. Umweg (km)</label>
                  <input
                    type="number" value={maxDetour} min={0} max={50} step={0.5}
                    onChange={(e) => setMaxDetour(parseFloat(e.target.value) || 0)}
                    data-testid="driver-detour-input"
                  />
                </div>
              </div>

              {vehicle && (
                <div className="do-vehicle">
                  <Car size={16} />
                  <span>{vehicle.make} {vehicle.model} — {vehicle.license_plate}</span>
                </div>
              )}
            </div>

            {statusMessage && <div className="do-status-msg">{statusMessage}</div>}

            <button
              className="do-go-btn"
              onClick={handleGoOnline}
              disabled={loading || !connected}
              data-testid="go-online-btn"
            >
              {loading ? <Loader2 size={20} className="spin" /> : <Radio size={20} />}
              {t('driver.goOnlineBtn', 'Online gehen — Mitfahrer empfangen')}
            </button>
          </div>
        ) : (
          /* ── Online Dashboard ── */
          <div className="do-live" data-testid="driver-live-dashboard">
            <div className="do-live-status">
              <div className="do-pulse" />
              <span>Live — Warte auf Mitfahrer-Anfragen...</span>
            </div>

            <div className="do-route-summary">
              <MapPin size={16} /> {startCity} → {destCity}
            </div>

            {/* Match Requests */}
            <div className="do-matches" data-testid="match-requests-list">
              <h3>Anfragen ({matchRequests.length})</h3>
              {matchRequests.length === 0 ? (
                <div className="do-empty">Noch keine Anfragen. Deine Route wird aktiv gematcht...</div>
              ) : (
                matchRequests.map((req, i) => (
                  <div key={i} className="do-match-card" data-testid={`match-request-${i}`}>
                    <div className="do-match-info">
                      <strong>{req.guest_name || 'Mitfahrer'}</strong>
                      <span className="do-match-detail">
                        Umweg: {req.detour_km} km | Entfernung zum Einstieg: {req.pickup_dist_km} km
                      </span>
                    </div>
                    <div className="do-match-actions">
                      <button className="do-accept" onClick={() => handleAccept(req)} data-testid={`accept-match-${i}`}>
                        <CheckCircle size={18} /> Annehmen
                      </button>
                      <button className="do-reject" onClick={() => handleReject(req)} data-testid={`reject-match-${i}`}>
                        <XCircle size={18} /> Ablehnen
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <button className="do-offline-btn" onClick={handleGoOffline} data-testid="go-offline-btn">
              Offline gehen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverOnline;
